import { HeartPulse, Pill, Stethoscope, FlaskConical } from 'lucide-react';
import ResqlyMark from './ResqlyMark';
import { isNativePlatform } from '@/lib/providerPush';

/**
 * Consumer authentication shell.
 * Keep the existing app-native presentation inside the Android WebView,
 * but give the custom domain a real responsive web portal layout.
 */
export default function CustomerAuthLayout({ children, footer }) {
  if (isNativePlatform()) {
    return (
      <div className="resqly-shell min-h-screen min-h-[100dvh]">
        <div className="resqly-frame flex flex-col min-h-screen min-h-[100dvh]">
          <div className="auth-hero relative px-6 pt-12 pb-10 text-white overflow-hidden">
            <div className="auth-hero-glow absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10" />
            <div className="auth-hero-glow absolute -left-12 bottom-0 h-40 w-40 rounded-full bg-cyan-300/10" />
            <div className="relative z-10 flex flex-col items-center text-center">
              <ResqlyMark size={56} className="drop-shadow-[0_8px_24px_rgba(0,0,0,0.25)]" />
              <h1 className="mt-5 text-2xl font-bold tracking-tight">Welcome to RESQLY</h1>
              <p className="mt-2 text-sm text-blue-100/90 font-medium">Healthcare simplified.</p>
            </div>
          </div>

          <div className="flex-1 px-6 pb-8 -mt-3">
            <div className="resqly-card auth-form-card p-5 shadow-[var(--resqly-shadow-lg)]">
              {children}
            </div>
            {footer}
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="grid min-h-screen lg:grid-cols-[minmax(0,1.08fr)_minmax(440px,0.92fr)]">
        <aside className="relative hidden overflow-hidden bg-gradient-to-br from-[#102c6b] via-[#1749a8] to-[#2877eb] px-10 py-10 text-white lg:flex lg:flex-col lg:justify-between xl:px-16 xl:py-12">
          <div className="pointer-events-none absolute -right-28 -top-20 h-96 w-96 rounded-full border border-white/10 bg-white/[0.04]" />
          <div className="pointer-events-none absolute -bottom-36 -left-28 h-[28rem] w-[28rem] rounded-full border border-white/10 bg-cyan-200/[0.06]" />

          <a href="/" className="relative z-10 flex w-fit items-center gap-3" aria-label="RESQLY home">
            <ResqlyMark size={44} />
            <span className="text-xl font-bold tracking-[0.18em]">RESQLY</span>
          </a>

          <div className="relative z-10 my-12 max-w-2xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-2 text-sm font-medium text-blue-50 backdrop-blur">
              <HeartPulse className="h-4 w-4" />
              Everyday healthcare, made simpler
            </div>
            <h2 className="max-w-xl text-4xl font-semibold leading-[1.12] tracking-tight xl:text-6xl">
              Better care starts with one place.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-7 text-blue-100 xl:text-lg">
              Find healthcare services, connect with care professionals, and manage your next step with less hassle.
            </p>

            <div className="mt-10 grid max-w-xl grid-cols-2 gap-3">
              <ServiceTile icon={Stethoscope} title="Doctor consultations" detail="Online and at home" />
              <ServiceTile icon={Pill} title="Medicine delivery" detail="Pharmacy at your door" />
              <ServiceTile icon={FlaskConical} title="Lab tests" detail="Book tests with ease" />
              <ServiceTile icon={HeartPulse} title="Home care" detail="Support when you need it" />
            </div>
          </div>

          <div className="relative z-10 flex items-center justify-between gap-6 border-t border-white/15 pt-5 text-sm text-blue-100">
            <span>Healthcare simplified.</span>
            <span className="text-white/70">Secure sign-in</span>
          </div>
        </aside>

        <section className="flex min-h-screen flex-col bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50 via-slate-50 to-white">
          <header className="flex items-center gap-3 px-6 py-6 sm:px-10 lg:hidden">
            <ResqlyMark size={38} />
            <span className="text-base font-bold tracking-[0.16em] text-blue-950">RESQLY</span>
          </header>

          <div className="flex flex-1 flex-col justify-center px-5 pb-10 pt-2 sm:px-10 lg:px-12 xl:px-16">
            <div className="mx-auto w-full max-w-[440px]">
              <div className="mb-7 hidden lg:block">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Your healthcare, connected</p>
                <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 xl:text-4xl">
                  Welcome to RESQLY
                </h1>
                <p className="mt-3 text-sm leading-6 text-slate-500">
                  Sign in to book services and keep your healthcare in one place.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-[0_24px_80px_rgba(15,35,80,0.09)] sm:p-8">
                <div className="mb-6 lg:hidden">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Your healthcare, connected</p>
                  <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                    Welcome to RESQLY
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    Sign in to book services and keep your healthcare in one place.
                  </p>
                </div>
                {children}
                <p className="mt-6 text-center text-xs leading-5 text-slate-400">
                  Your account is protected with secure authentication.
                </p>
              </div>
              {footer}
            </div>
          </div>

          <footer className="px-6 pb-5 text-center text-xs text-slate-400 sm:px-10">
            RESQLY · Healthcare, simplified
          </footer>
        </section>
      </div>
    </main>
  );
}

function ServiceTile({ icon: Icon, title, detail }) {
  return (
    <div className="flex min-h-[90px] items-start gap-3 rounded-2xl border border-white/15 bg-white/[0.09] p-4 backdrop-blur-sm">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 pt-0.5">
        <span className="block text-sm font-semibold leading-5 text-white">{title}</span>
        <span className="mt-1 block text-xs leading-4 text-blue-100">{detail}</span>
      </span>
    </div>
  );
}
